import { IVA_RATE } from '@repo/shared-types';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QuotationStatus } from '@/features/sales/quotation-status';
import type {
  ConvertQuotationInput,
  CreateQuotationFormValues,
  Quotation,
  QuotationDetail,
  QuotationsListResponse,
} from '@/features/sales/types/quotation.types';
import { apiFetch } from '@/lib/api';

/**
 * Quotations — TanStack Query hooks.
 *
 * Endpoints covered:
 *   GET    /quotations                  list (paginated, status filter)
 *   POST   /quotations                  create (DRAFT)
 *   GET    /quotations/:id              detail (with items)
 *   PATCH  /quotations/:id              update header
 *   DELETE /quotations/:id              reject (lifecycle)
 *   PATCH  /quotations/:id/send         mark SENT
 *   PATCH  /quotations/:id/accept       mark ACCEPTED
 *   PATCH  /quotations/:id/reject       mark REJECTED
 *   POST   /quotations/:id/convert      convert to sales order
 *
 * CRITICAL field-gating: WAREHOUSE sees the quotation WITHOUT
 * prices. The detail view narrows on `'subtotal' in q` before
 * rendering any monetary column.
 */

export type QuotationsSortKey = 'quotationNumber' | 'createdAt' | 'totalAmount';
export type SortDirection = 'asc' | 'desc';

export interface QuotationsListParams {
  page: number;
  pageSize: number;
  sort?: QuotationsSortKey;
  dir?: SortDirection;
  // sdd/quotation-status-catalog: narrowed from `string` to the
  // typed enum so the chip filter cannot pass a random UUID.
  statusId?: QuotationStatus;
  clientId?: string;
  sellerId?: string;
}

function buildQuery(params: QuotationsListParams): string {
  const sp = new URLSearchParams();
  sp.set('page', String(params.page));
  sp.set('pageSize', String(params.pageSize));
  if (params.sort) sp.set('sort', params.sort);
  if (params.dir) sp.set('dir', params.dir);
  if (params.statusId) sp.set('statusId', params.statusId);
  if (params.clientId) sp.set('clientId', params.clientId);
  if (params.sellerId) sp.set('sellerId', params.sellerId);
  return sp.toString();
}

export const quotationsKeys = {
  all: ['quotations'] as const,
  list: (params: QuotationsListParams) => ['quotations', 'list', params] as const,
  detail: (id: string) => ['quotations', 'detail', id] as const,
};

export function useQuotations(params: QuotationsListParams) {
  return useQuery({
    queryKey: quotationsKeys.list(params),
    queryFn: () => apiFetch<QuotationsListResponse>(`/quotations?${buildQuery(params)}`),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useQuotation(id: string | null) {
  return useQuery({
    queryKey: quotationsKeys.detail(id ?? ''),
    queryFn: () => apiFetch<QuotationDetail>(`/quotations/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateQuotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateQuotationFormValues) => {
      const subtotal = input.items.reduce(
        (acc, it) => acc + Number(it.unitPrice ?? 0) * Number(it.quantity ?? 0),
        0,
      );
      // 16% IVA Venezuela — mirrors QuotationForm.tsx (must agree
      // byte-for-byte or the form's running total diverges from the
      // wire payload). See packages/shared-types/src/tax.ts for the
      // reconciliation plan.
      const tax = input.includesTax ? Number(IVA_RATE) * subtotal : 0;
      const total = subtotal + tax;
      const wire: Record<string, unknown> = {
        clientId: input.clientId,
        sellerId: input.sellerId,
        currencyId: input.currencyId,
        includesTax: input.includesTax,
        validUntil: input.validUntil ?? null,
        notes: input.notes ?? null,
        subtotal: subtotal.toFixed(4),
        taxAmount: tax.toFixed(4),
        totalAmount: total.toFixed(4),
        items: input.items.map((it) => ({
          productId: it.productId,
          quantity: it.quantity,
          unitPrice: it.unitPrice,
          subtotal: (Number(it.unitPrice ?? 0) * Number(it.quantity ?? 0)).toFixed(4),
          notes: it.notes ?? null,
        })),
      };
      // Financing metadata — propagated to the convert step when
      // the customer accepts the quotation. We only attach keys
      // that have a value so a CONTADO quotation sends the same
      // shape it always did (preserves the historical wire).
      if (input.isFinanced) {
        wire['isFinanced'] = true;
        if (input.totalInstallments !== undefined)
          wire['totalInstallments'] = input.totalInstallments;
        if (input.downPayment !== undefined && input.downPayment !== '')
          wire['downPayment'] = input.downPayment;
        if (input.frequencyDays !== undefined) wire['frequencyDays'] = input.frequencyDays;
        if (input.startDate) wire['startDate'] = input.startDate;
      } else {
        wire['isFinanced'] = false;
      }
      return apiFetch<QuotationDetail>('/quotations', {
        method: 'POST',
        body: JSON.stringify(wire),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: quotationsKeys.all }),
  });
}

export function useUpdateQuotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      notes,
      validUntil,
      includesTax,
    }: {
      id: string;
      notes?: string | null;
      validUntil?: string | null;
      includesTax?: boolean;
    }) =>
      apiFetch<QuotationDetail>(`/quotations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          ...(notes !== undefined ? { notes } : {}),
          ...(validUntil !== undefined ? { validUntil } : {}),
          ...(includesTax !== undefined ? { includesTax } : {}),
        }),
      }),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: quotationsKeys.all });
      qc.invalidateQueries({ queryKey: quotationsKeys.detail(id) });
    },
  });
}

export function useSendQuotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<QuotationDetail>(`/quotations/${id}/send`, { method: 'PATCH' }),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: quotationsKeys.all });
      qc.invalidateQueries({ queryKey: quotationsKeys.detail(id) });
    },
  });
}

export function useAcceptQuotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<QuotationDetail>(`/quotations/${id}/accept`, { method: 'PATCH' }),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: quotationsKeys.all });
      qc.invalidateQueries({ queryKey: quotationsKeys.detail(id) });
    },
  });
}

export function useRejectQuotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<Quotation>(`/quotations/${id}/reject`, { method: 'PATCH' }),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: quotationsKeys.all });
      qc.invalidateQueries({ queryKey: quotationsKeys.detail(id) });
    },
  });
}

export function useConvertQuotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, options }: { id: string; options: ConvertQuotationInput }) =>
      apiFetch<{ order: Quotation; items: unknown[]; snapshots: unknown[] }>(
        `/quotations/${id}/convert`,
        {
          method: 'POST',
          body: JSON.stringify(options),
        },
      ),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: quotationsKeys.all });
      qc.invalidateQueries({ queryKey: quotationsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}
